import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Trash2, Search, Users, Shield, Building, FileText, FileJson, Folder, RefreshCw } from 'lucide-react'
import type { SavedSociety } from '@creanizer/types'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { ExportImportButtons } from '../../../../components/common'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedSocietiesListProps {
  societies: SavedSociety[]
  onLoad: (society: SavedSociety) => void
  onUpdate?: (society: SavedSociety) => void
}

export function SavedSocietiesList({ societies, onLoad, onUpdate }: SavedSocietiesListProps) {
  const { 
    removeSociety, 
    clearAllSocieties, 
    exportAsText, 
    exportAsJSON, 
    importFromJSON,
    exportSingleAsText,
    exportSingleAsJSON 
  } = useSocietyStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredSocieties = societies
  if (selectedFolderId === 'all') {
    folderFilteredSocieties = societies
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('society')
    folders.forEach(folder => {
      getItemsInFolder('society', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredSocieties = societies.filter(society => !allFolderItemIds.has(society.id))
  } else {
    const itemIds = getItemsInFolder('society', selectedFolderId)
    folderFilteredSocieties = societies.filter(society => itemIds.includes(society.id))
  }
  
  // 検索とタイプによるフィルタリング
  const filteredSocieties = folderFilteredSocieties.filter((society) => {
    const matchesSearch = society.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      society.result.basicInfo.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      society.result.basicInfo.summary.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesType = !selectedType || society.result.basicInfo.type === selectedType
    
    return matchesSearch && matchesType
  })
  
  const societyTypes = Array.from(new Set(societies.map(s => s.result.basicInfo.type)))
  
  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済み組織 ({societies.length}/200)
        </h2>
      </div>
      
      {/* 検索・フィルター */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-2 flex-1">
          <div className="flex-1 relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="組織名や説明で検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>
        {societyTypes.length > 0 && (
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border rounded-md min-w-[120px]"
          >
            <option value="">全タイプ</option>
            {societyTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {/* エクスポート・インポート・削除ボタン */}
      <div className="flex flex-wrap gap-2 mb-4">
        <ExportImportButtons
          dataTypeName="組織"
          onExportText={exportAsText}
          onExportJSON={exportAsJSON}
          onImportJSON={importFromJSON}
        />
        <Button 
          onClick={clearAllSocieties} 
          variant="destructive" 
          size="sm"
          disabled={societies.length === 0}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          全て削除
        </Button>
      </div>
      
      {/* 組織リスト */}
      <div className="flex gap-4">
        {/* フォルダサイドバー */}
        {showFolders && (
          <div className="w-48 border-r pr-4">
            <FolderManager
              generatorType="society"
              items={societies}
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
              draggedItemId={draggedItemId}
              onItemMove={(itemId, toFolderId) => {
                const currentFolder = getItemFolder('society', itemId)
                moveItemBetweenFolders('society', itemId, currentFolder?.id || null, toFolderId)
              }}
            />
          </div>
        )}
        
        {/* 組織リスト */}
        <div className="flex-1">
          {filteredSocieties.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {searchTerm ? '検索結果がありません' : '保存された組織がありません'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-3">
                {filteredSocieties.map((society) => (
                  <div 
                    key={society.id} 
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-move"
                    draggable
                    onDragStart={(e) => {
                      setDraggedItemId(society.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => {
                      setDraggedItemId(null)
                    }}
                    style={{
                      opacity: draggedItemId === society.id ? 0.5 : 1
                    }}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg flex items-center gap-2 flex-wrap">
                      {society.result.basicInfo.name}
                      {society.result.basicInfo.aliases && society.result.basicInfo.aliases.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          +{society.result.basicInfo.aliases.length}
                        </Badge>
                      )}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        <Building className="h-3 w-3 mr-1" />
                        {society.result.basicInfo.type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <Shield className="h-3 w-3 mr-1" />
                        {society.result.basicInfo.scale}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                      {society.result.basicInfo.summary}
                    </p>
                    
                    {society.result.basicInfo.motto && (
                      <p className="text-sm italic text-muted-foreground mt-2">
                        「{society.result.basicInfo.motto}」
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                      <span>メンバー: {society.result.structure.memberCount}</span>
                      {society.result.members?.importantMembers?.[0] && (
                        <span>リーダー: {society.result.members.importantMembers[0].name}</span>
                      )}
                      <span>{new Date(society.createdAt).toLocaleDateString('ja-JP')}</span>
                    </div>
                    
                    {/* フォルダ選択 */}
                    <div className="mt-2 sm:w-32">
                      <FolderMoveSelect
                        generatorType="society"
                        itemId={society.id}
                        currentFolderId={getItemFolder('society', society.id)?.id}
                        onMove={() => {}}
                      />
                    </div>
                  </div>
                  
                  {/* アクションボタン - PC */}
                  <div className="hidden sm:flex gap-1">
                    <Button
                      onClick={() => onLoad(society)}
                      size="sm"
                      variant="default"
                    >
                      読込
                    </Button>
                    {onUpdate && (
                      <Button
                        onClick={() => onUpdate(society)}
                        size="sm"
                        variant="outline"
                        title="情報更新モードで読み込む"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        更新
                      </Button>
                    )}
                    <Button
                      onClick={() => exportSingleAsText(society.id)}
                      size="sm"
                      variant="outline"
                      title="テキストとしてエクスポート"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => exportSingleAsJSON(society.id)}
                      size="sm"
                      variant="outline"
                      title="JSONとしてエクスポート"
                    >
                      <FileJson className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => removeSociety(society.id)}
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* アクションボタン - モバイル */}
                <div className="flex sm:hidden gap-1 mt-3 flex-wrap">
                  <Button
                    onClick={() => onLoad(society)}
                    size="sm"
                    variant="default"
                    className="flex-1"
                  >
                    読込
                  </Button>
                  {onUpdate && (
                    <Button
                      onClick={() => onUpdate(society)}
                      size="sm"
                      variant="outline"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    onClick={() => exportSingleAsText(society.id)}
                    size="sm"
                    variant="outline"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => exportSingleAsJSON(society.id)}
                    size="sm"
                    variant="outline"
                  >
                    <FileJson className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => removeSociety(society.id)}
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  )
}